
import { createClient, SupabaseClient } from '@supabase/supabase-js';
import type { Database } from './types';
import { config, currentEnvironment, envLog } from '@/config/environment';

/**
 * Environment-aware Supabase client factory
 * Creates Supabase clients with environment-specific configurations
 */
export class EnvironmentSupabaseClient {
  private static instance: SupabaseClient<Database> | null = null;

  /**
   * Get the singleton Supabase client instance
   */
  static getInstance(): SupabaseClient<Database> {
    if (!this.instance) {
      this.instance = this.createClient();
    }
    return this.instance;
  }

  /**
   * Create a new Supabase client with current environment configuration
   */
  static createClient(): SupabaseClient<Database> {
    const { url, anonKey, maxConnections, enableRealtime } = config.database;
    
    envLog.info('Creating Supabase client for environment:', {
      environment: currentEnvironment,
      url: url,
      enableRealtime: enableRealtime,
      maxConnections: maxConnections
    });

    const client = createClient<Database>(url, anonKey, {
      db: {
        schema: 'public'
      },
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true
      },
      realtime: {
        params: {
          eventsPerSecond: enableRealtime ? 50 : 0
        }
      },
      global: {
        headers: {
          'x-client-info': `nationcard-${currentEnvironment}`
        }
      }
    });

    return client;
  }

  /**
   * Create an admin client (for server-side operations)
   * This would use the service role key if available
   */
  static createAdminClient(): SupabaseClient<Database> | null {
    const { url, serviceRoleKey } = config.database;
    
    if (!serviceRoleKey) {
      envLog.warn('Service role key not available for admin client');
      return null;
    }

    envLog.info('Creating Supabase admin client for environment:', currentEnvironment);

    return createClient<Database>(url, serviceRoleKey, {
      auth: {
        autoRefreshToken: false,
        persistSession: false
      },
      global: {
        headers: {
          'x-client-info': `nationcard-${currentEnvironment}-admin`
        }
      }
    });
  }

  /**
   * Get connection info for debugging
   */
  static getConnectionInfo() {
    return {
      environment: currentEnvironment,
      url: config.database.url,
      maxConnections: config.database.maxConnections,
      enableRealtime: config.database.enableRealtime,
      projectRef: config.database.url.split('//')[1]?.split('.')[0] || 'unknown'
    };
  }

  /**
   * Test the connection to Supabase
   */
  static async testConnection(): Promise<boolean> {
    try {
      const client = this.getInstance();
      const { data, error } = await client.from('countries').select('count').limit(1);
      
      if (error) {
        envLog.error('Supabase connection test failed:', error);
        return false;
      }

      envLog.info('Supabase connection test successful');
      return true;
    } catch (error) {
      envLog.error('Supabase connection test error:', error);
      return false;
    }
  }
}

// Export the main client instance for backward compatibility
export const environmentSupabase = EnvironmentSupabaseClient.getInstance();
